/**
 * Exportacao CSV da lista de leads -- gerada no navegador, sem rede.
 *
 * Decisoes:
 *  - separador `;` e BOM UTF-8: e o que o Excel em pt-BR abre direto, com
 *    acento certo e sem juntar tudo numa coluna (a virgula e o decimal aqui);
 *  - o telefone sai MASCARADO, igual a tela. O painel nao mostra o numero
 *    completo na lista, e uma exportacao nao pode ser a porta dos fundos para
 *    isso. Se o dono quiser o E.164 no arquivo, e decisao de produto (registrada
 *    no relatorio), nao um detalhe de implementacao;
 *  - dinheiro passa por `formatBRLFromCents`: e o unico lugar que divide por 100;
 *  - sai o que esta na tela: mesmas linhas, mesma ordem, mesmos filtros.
 */

import { formatBRLFromCents, formatDateTime, maskPhoneDisplay, PROVIDER_LABEL, VALOR_AUSENTE } from '../../lib/format';
import type { LeadListRow } from './types';
import { CANAL, DESFECHO_LABEL, FUNIL_LABEL, SITUACAO } from './vocabulario';

const SEPARADOR = ';';
// Montado por codigo, e nao como literal: um U+FEFF invisivel dentro de aspas
// e o tipo de caractere que editor e formatador apagam sem avisar.
const BOM = String.fromCharCode(0xfeff);

const CABECALHO: readonly string[] = [
  'Lead',
  'Telefone (mascarado)',
  'Produto',
  'Origem',
  'Funil',
  'Valor',
  'Alto valor',
  'Situação',
  'Contatos',
  'Último contato',
  'Último contato em',
  'Próximo toque',
  'Próximo toque em',
  'Saiu em',
  'Recuperado em',
  'Id',
];

function celula(valor: string): string {
  const precisaAspas = valor.includes(SEPARADOR) || valor.includes('"') || valor.includes('\n') || valor.includes('\r');
  return precisaAspas ? `"${valor.replace(/"/g, '""')}"` : valor;
}

export function leadsParaCsv(leads: readonly LeadListRow[]): string {
  const linhas = leads.map((lead) =>
    [
      lead.name ?? VALOR_AUSENTE,
      maskPhoneDisplay(lead.phoneE164),
      lead.productName ?? VALOR_AUSENTE,
      PROVIDER_LABEL[lead.provider],
      FUNIL_LABEL[lead.funnel],
      formatBRLFromCents(lead.amountCents),
      lead.highValue ? 'sim' : 'não',
      SITUACAO[lead.situacao].label,
      String(lead.contacts),
      lead.lastContact === null ? '' : `${CANAL[lead.lastContact.channel].label} ${lead.lastContact.touchLabel} · ${DESFECHO_LABEL[lead.lastContact.outcome]}`.trim(),
      lead.lastContact === null ? '' : formatDateTime(lead.lastContact.at),
      lead.nextTouch === null ? '' : `${CANAL[lead.nextTouch.channel].label} ${lead.nextTouch.label}`,
      lead.nextTouch === null ? '' : formatDateTime(lead.nextTouch.scheduledAt),
      formatDateTime(lead.abandonedAt),
      lead.recoveredAt === null ? '' : formatDateTime(lead.recoveredAt),
      lead.id,
    ]
      .map(celula)
      .join(SEPARADOR),
  );
  return `${BOM}${[CABECALHO.map(celula).join(SEPARADOR), ...linhas].join('\r\n')}\r\n`;
}

/** Dispara o download de um texto como arquivo. O objeto URL e liberado logo depois do clique. */
export function baixarArquivoTexto(nome: string, conteudo: string, tipo = 'text/csv;charset=utf-8'): void {
  const blob = new Blob([conteudo], { type: tipo });
  const url = URL.createObjectURL(blob);
  const ancora = document.createElement('a');
  ancora.href = url;
  ancora.download = nome;
  ancora.rel = 'noopener';
  document.body.appendChild(ancora);
  ancora.click();
  document.body.removeChild(ancora);
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** "leads-escola-fluente-2026-09-24.csv": sem acento, sem espaco, data no fim. */
export function nomeDoArquivoCsv(slugEmpresa: string, data: Date): string {
  const y = data.getFullYear();
  const m = String(data.getMonth() + 1).padStart(2, '0');
  const d = String(data.getDate()).padStart(2, '0');
  const slug = slugEmpresa
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return `leads-${slug === '' ? 'empresa' : slug}-${y}-${m}-${d}.csv`;
}
